import React, { useEffect, useState } from "react";
import { BaseAuthContainer } from "./BaseAuthContainer";
import { User } from "firebase/auth";
import { auth, signInAnonymous, db } from "./firebase";
import { useAppDispatch, useAppSelector } from "./redux/store";
import { clearUserName, setUserProfile } from "./redux/userSlice";
import Spinner from "./common/Spinner";
import AuthScreensContainer from "./screens/Auth/containers/AuthScreensContainer";
import {
  logAnonymousSignIn,
  setUserAnalyticsProperties,
} from "./utils/analyticsUtils";
import { registerPushTokenSilently } from "./services/notifications";
import { saveClientInfo, saveUserTimezone } from "./services/userService";
import { doc, onSnapshot } from "firebase/firestore";
import { View, Text } from "react-native";

// Original AuthContainer for backward compatibility
type AuthContainerProps = {
  children: React.ReactNode;
};

export const AuthContainer = ({ children }: AuthContainerProps) => {
  const dispatch = useAppDispatch();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const banned = useAppSelector((state) => state.user.banned);

  const handleLogout = async () => {
    setCurrentUser(null);
    dispatch(clearUserName());
    // debounce automatic anonymous sign-in to avoid reacting to
    // transient null states during profile updates. Wait briefly and
    // only sign in anonymously if there's still no current user.
    // await new Promise((resolve) => setTimeout(resolve, 500));
    // if (!auth.currentUser) {
    await signInAnonymous();
    logAnonymousSignIn();
    // }
  };

  const handleUpdateUser = async (user: User) => {
    setCurrentUser(user);
    // Set user properties for analytics
    setUserAnalyticsProperties(user.uid);
    // Refresh the device push token on launch/login without prompting. The
    // in-app EnableNotificationsPrompt handles first-time permission requests.
    registerPushTokenSilently(user.uid);
    // Keep the user's timezone current for reminder emails (#76), and record
    // which app version/OTA this platform runs. Skip anonymous sessions — they
    // have no user doc to write.
    if (!user.isAnonymous) {
      saveUserTimezone(user.uid);
      saveClientInfo(user.uid);
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    const userRef = doc(db, "users", currentUser.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const userName = currentUser.displayName || data.name || "";
          dispatch(
            setUserProfile({
              name: userName.trim(),
              admin: data.admin === true,
              banned: data.banned === true,
              testAccount: data.testAccount === true,
            }),
          );
        }
      },
      (error) => {
        console.error("Error listening to user profile:", error);
      },
    );

    return unsubscribe;
  }, [currentUser, dispatch]);

  if (banned) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#fff",
          padding: 24,
        }}
      >
        <Text
          style={{
            fontSize: 26,
            fontWeight: "bold",
            color: "#ff4444",
            marginBottom: 12,
          }}
        >
          Account Banned
        </Text>
        <Text
          style={{
            fontSize: 16,
            color: "#666",
            textAlign: "center",
            maxWidth: 320,
            lineHeight: 22,
          }}
        >
          Your account has been banned by an administrator for violating
          community guidelines.
        </Text>
      </View>
    );
  }

  return (
    <BaseAuthContainer
      loadingComponent={Spinner}
      authComponent={Spinner}
      setDisplayNameComponent={AuthScreensContainer}
      firebaseAuth={auth}
      onAuthenticated={handleUpdateUser}
      onUnAuthenticated={handleLogout}
    >
      {children}
    </BaseAuthContainer>
  );
};
